import type { Battle } from "@loot-os/api-contract";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Link } from "@tanstack/react-router";

import { SeatName } from "./SeatName.js";

/**
 * The lobby stage: every seat as a tile. An empty seat offers Join to a signed-in shopper who
 * holds no seat, and Add bot to the creator; the footer offers Start now to the creator and
 * Start to anyone once the lobby is stale.
 */
export function BattleSeatTiles({
  battle,
  viewerEndUserId,
  signedIn,
  isCreator,
  canStartAsStranger,
  pending,
  onJoin,
  onAddBot,
  onStart,
}: {
  battle: Battle;
  viewerEndUserId: string | null | undefined;
  signedIn: boolean;
  isCreator: boolean;
  canStartAsStranger: boolean;
  pending: boolean;
  onJoin: (seatIndex: number) => void;
  onAddBot: (seatIndex: number) => void;
  onStart: () => void;
}) {
  const seated = battle.me !== null;
  const empty = battle.seats.filter((s) => s.kind === "empty").length;
  return (
    <div className="grid gap-4">
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${Math.min(battle.playerCount, 3)}, minmax(0, 1fr))`,
        }}
      >
        {battle.seats.map((seat) => (
          <div
            key={seat.seatIndex}
            className={`flex min-h-28 flex-col items-center justify-center gap-2 rounded-xl border p-3 text-center ${
              seat.kind === "empty" ? "border-dashed border-border" : "border-border bg-card"
            }`}
          >
            <span className="text-xs text-muted-foreground">Seat {seat.seatIndex + 1}</span>
            <SeatName seat={seat} viewerEndUserId={viewerEndUserId} />
            {seat.kind === "empty" && battle.status === "created" ? (
              <div className="flex flex-wrap justify-center gap-1.5">
                {!signedIn ? (
                  <Link to="/login" className={buttonVariants({ size: "sm" })}>
                    Sign in to join
                  </Link>
                ) : !seated ? (
                  <Button size="sm" disabled={pending} onClick={() => onJoin(seat.seatIndex)}>
                    Join seat
                  </Button>
                ) : null}
                {isCreator ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={pending}
                    onClick={() => onAddBot(seat.seatIndex)}
                  >
                    Add bot
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {battle.status === "created" ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            Waiting for {empty} more {empty === 1 ? "player" : "players"}…
          </span>
          {isCreator ? (
            <Button size="sm" disabled={pending} onClick={onStart}>
              Start now with bots
            </Button>
          ) : canStartAsStranger && signedIn ? (
            <Button size="sm" variant="secondary" disabled={pending} onClick={onStart}>
              Start with bots
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
